import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { ensureCurrentUser } from '../../util/data';
import { WarningIcon, SuccessIcon, IconLockL, IconPenM } from '../../icons';
import { isChangeEmailTakenError, isChangeEmailWrongPassword } from '../../util/errors';
import { FieldPhoneNumberInput, Form, Button, FieldTextInput, FieldSelect } from '../../components';
import { countryCodesFormatterConfig } from '../../components/FieldPhoneNumberInput/fiFormatter';
import { initPhoneCode } from './util/initPhoneCode';
import VerifyPhoneNumberBlock from './VerifyPhoneNumberBlock';
import ContactPreferenceModal from './ContactPreferenceModal';
import css from './ContactDetailsForm.css';
import { useIntl } from 'react-intl';

const SHOW_EMAIL_SENT_TIMEOUT = 2000;

const windowDefined = typeof window !== 'undefined';
const isMobile = windowDefined && window.innerWidth < 768;

const ContactDetailsForm = props => {
    const [showVerificationEmailSentMessage, setShowVerificationEmailSentMessage] = useState(false);
    const [isContactPreferenceModalOpen, setIsContactPreferenceModalOpen] = useState(false);
    const emailSentTimeoutId = useRef(null);
    const submittedValuesRef = useRef({});
    const intl = useIntl();

    useEffect(() => {
        return () => {
            if (emailSentTimeoutId.current) {
                window.clearTimeout(emailSentTimeoutId.current);
            }
        };
    }, []);

    const handleResendVerificationEmail = useCallback(() => {
        setShowVerificationEmailSentMessage(true);

        props.onResendVerificationEmail().then(() => {
            // show "verification email sent" text for a bit longer.
            emailSentTimeoutId.current = window.setTimeout(() => {
                setShowVerificationEmailSentMessage(false);
            }, SHOW_EMAIL_SENT_TIMEOUT);
        });
    }, [props]);

    return (
        <FinalForm
            {...props}
            initialValues={{
                ...(props.initialValues || {}),
                countryCode: initPhoneCode(props.savedCountryCode, props.currentUser),
            }}
            render={fieldRenderProps => {
                const {
                    rootClassName,
                    className,
                    saveEmailError,
                    savePhoneNumberError,
                    currentUser,
                    inProgress,
                    values,
                    sendVerificationOtpError,
                    sendVerificationOtpInProgress,
                    form,
                } = fieldRenderProps;

                const { valid: phoneValid, dirty: phoneDirty } = form.getFieldState(
                    'phoneNumber'
                ) || {
                    valid: false,
                };
                const { valid: emailValid } = form.getFieldState('email') || { valid: false };
                const { valid: currentPasswordValid } = form.getFieldState('currentPassword') || {
                    valid: false,
                };

                const { email, phoneNumber, countryCode, currentPassword } = values || {};

                const user = ensureCurrentUser(currentUser);

                if (!user.id) {
                    return null;
                }

                const { email: currentEmail, emailVerified, profile } = user.attributes;
                const { phoneVerified } = profile.publicData || {};

                const emailChanged = currentEmail !== email;

                const emailPlaceholder = intl.formatMessage({
                    id: 'ContactDetailsForm.emailLabel',
                });

                const emailRequiredMessage = intl.formatMessage({
                    id: 'ContactDetailsForm.emailRequired',
                });

                const fieldRequiredMessage = intl.formatMessage({
                    id: 'ContactDetailsForm.fieldRequired',
                });

                const emailRequired = validators.required(emailRequiredMessage);
                const emailInvalidMessage = intl.formatMessage({
                    id: 'ContactDetailsForm.emailInvalid',
                });
                const emailValidValidator = validators.emailFormatValid(emailInvalidMessage);

                const emailTouched = submittedValuesRef.current.email !== values.email;
                const emailTakenErrorText = isChangeEmailTakenError(saveEmailError)
                    ? intl.formatMessage({ id: 'ContactDetailsForm.emailTakenError' })
                    : null;

                // phone
                const protectedData = profile.protectedData || {};
                const currentPhoneNumber =
                    protectedData.phoneNumber &&
                    protectedData.phoneNumber.replace(values.countryCode, '');

                // has the phone number changed
                const phoneNumberChanged = phoneNumber && currentPhoneNumber !== phoneNumber;

                const phonePlaceholder = intl.formatMessage({
                    id: 'ContactDetailsForm.phonePlaceholder',
                });

                const passwordFailedMessage = intl.formatMessage({
                    id: 'ContactDetailsForm.passwordFailed',
                });

                const passwordErrorText = isChangeEmailWrongPassword(saveEmailError)
                    ? passwordFailedMessage
                    : null;

                const confirmClasses = classNames(css.confirmChangesSection, {
                    [css.confirmChangesSectionVisible]: emailChanged,
                });

                // generic error
                const isGenericEmailError =
                    saveEmailError && !(emailTakenErrorText || passwordErrorText);

                let genericError = null;

                if (isGenericEmailError && savePhoneNumberError) {
                    genericError = (
                        <span className={css.error}>
                            <FormattedMessage id="ContactDetailsForm.genericFailure" />
                        </span>
                    );
                } else if (isGenericEmailError) {
                    genericError = (
                        <span className={css.error}>
                            <FormattedMessage id="ContactDetailsForm.genericEmailFailure" />
                        </span>
                    );
                } else if (savePhoneNumberError) {
                    genericError = (
                        <span className={css.error}>
                            <FormattedMessage id="ContactDetailsForm.genericPhoneNumberFailure" />
                        </span>
                    );
                }

                let otpError = null;
                if (sendVerificationOtpError) {
                    otpError = (
                        <span className={css.error}>
                            We couldn't send the verification code to this number, please change the
                            number and try again.
                        </span>
                    );
                }

                const classes = classNames(rootClassName || css.root, className);
                const submittedOnce = Object.keys(submittedValuesRef.current).length > 0;
                const pristineSinceLastSubmit =
                    submittedOnce && isEqual(values, submittedValuesRef.current);
                /** password field should be considered when an email field has been changed */
                const passwordIsNotValid =
                    emailChanged && (!currentPassword || !currentPasswordValid);
                const emailIsNotValid = emailChanged && !emailValid;
                const phoneIsNotValid = phoneNumberChanged && !phoneValid;
                const pristine = !phoneNumberChanged && !emailChanged;
                const submitDisabled =
                    pristine ||
                    pristineSinceLastSubmit ||
                    inProgress ||
                    /** password field is visible when email has been changed */
                    (emailChanged && passwordIsNotValid) ||
                    emailIsNotValid ||
                    phoneIsNotValid;

                return (
                    <Form className={classes} onSubmit={() => null}>
                        <div className={css.contactDetailsSection}>
                            <div className={css.phoneInfoHolder}>
                                <FieldSelect
                                    name="countryCode"
                                    id="countryCode"
                                    validate={validators.required(fieldRequiredMessage)}
                                    placeholder="Vorwahl"
                                    form={form}
                                    optionsList={[
                                        { label: '+41', value: '+41' },
                                        { label: '+49', value: '+49' },
                                        { label: '+43', value: '+43' },
                                    ]}
                                    notifyOnChange={() => {
                                        const maxLengthDefined =
                                            countryCodesFormatterConfig[countryCode];

                                        if (phoneDirty && maxLengthDefined) {
                                            form.change(
                                                'phoneNumber',
                                                phoneNumber.slice(0, maxLengthDefined)
                                            );
                                        }
                                    }}
                                />

                                <FieldPhoneNumberInput
                                    name="phoneNumber"
                                    id="phoneNumber"
                                    type={isMobile ? 'tel' : 'text'}
                                    placeholder={phonePlaceholder}
                                    validate={validators.required(fieldRequiredMessage)}
                                    countryCode={countryCode}
                                    onBlur={() => {
                                        if (!phoneNumber) return;

                                        const precedingZeroMissed = !/^0/.test(phoneNumber);

                                        if (precedingZeroMissed) {
                                            form.change('phoneNumber', '0' + phoneNumber);
                                        }
                                        /**
                                         * Clear errors & loadings
                                         */
                                        props.discardErrors();
                                    }}
                                />
                            </div>
                            <div
                                className={classNames(css.verificationBlock, {
                                    [css.verified]: !!phoneVerified,
                                })}
                            >
                                {phoneVerified ? (
                                    <SuccessIcon />
                                ) : (
                                    <WarningIcon rootClassName={css.warningIcon} />
                                )}
                                <div>
                                    <FormattedMessage
                                        id={
                                            phoneVerified
                                                ? 'ContactDetailsForm.phoneVerifiedMessage'
                                                : 'ContactDetailsForm.phoneNotVerifiedMessage'
                                        }
                                    />

                                    {!phoneVerified && (
                                        <VerifyPhoneNumberBlock
                                            onSubmitContactDetails={props.onSubmit}
                                        />
                                    )}
                                </div>
                                {phoneVerified && (
                                    <>
                                        <p
                                            className={css.contactPreferenceBlock}
                                            role="button"
                                            tabIndex="0"
                                            aria-label="Kontaktpräferenz"
                                            onClick={() => setIsContactPreferenceModalOpen(true)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setIsContactPreferenceModalOpen(true);
                                                }
                                            }}
                                        >
                                            <IconPenM />
                                            <span>Kontaktpräferenz</span>
                                        </p>
                                        <ContactPreferenceModal
                                            isOpen={isContactPreferenceModalOpen}
                                            onClose={() => setIsContactPreferenceModalOpen(false)}
                                        />
                                    </>
                                )}
                            </div>

                            <FieldTextInput
                                type="email"
                                name="email"
                                id="email"
                                placeholder={emailPlaceholder}
                                validate={validators.composeValidators(
                                    emailRequired,
                                    emailValidValidator
                                )}
                                handleBlur={() => {
                                    /**
                                     * Clear errors & loadings
                                     */
                                    props.discardErrors();
                                }}
                                customErrorText={emailTouched ? null : emailTakenErrorText}
                            />
                            <p
                                className={`${css.verificationBlock} ${emailVerified &&
                                    css.verified}`}
                            >
                                {emailVerified ? (
                                    <SuccessIcon />
                                ) : (
                                    <WarningIcon rootClassName={css.warningIcon} />
                                )}
                                <div>
                                    <FormattedMessage
                                        id={
                                            emailVerified
                                                ? 'ContactDetailsForm.emailVerifiedMessage'
                                                : showVerificationEmailSentMessage
                                                ? 'ContactDetailsForm.emailSent'
                                                : 'ContactDetailsForm.emailNotVerifiedMessage'
                                        }
                                    />
                                    {!emailVerified && (
                                        <p
                                            className={css.verificationLink}
                                            onClick={handleResendVerificationEmail}
                                        >
                                            <FormattedMessage id="ContactDetailsForm.verifyEmailLink" />
                                        </p>
                                    )}
                                </div>
                            </p>
                            {sendVerificationOtpInProgress && (
                                <span className={css.emailVerified}>Verifizierungscode senden</span>
                            )}
                            {otpError}
                        </div>

                        <div className={confirmClasses}>
                            <FieldTextInput
                                className={css.password}
                                type="password"
                                id="currentPassword"
                                name="currentPassword"
                                autoComplete="current-password"
                                placeholder="Passwort"
                                validate={validators.composeValidators(
                                    validators.requiredStringNoTrim(
                                        intl.formatMessage({
                                            id: 'PasswordChangeForm.passwordRequired',
                                        })
                                    ),
                                    validators.minLength(
                                        intl.formatMessage(
                                            {
                                                id: 'PasswordChangeForm.passwordTooShort',
                                            },
                                            {
                                                minLength: validators.PASSWORD_MIN_LENGTH,
                                            }
                                        ),
                                        validators.PASSWORD_MIN_LENGTH
                                    ),
                                    validators.maxLength(
                                        intl.formatMessage(
                                            {
                                                id: 'PasswordChangeForm.passwordTooLong',
                                            },
                                            {
                                                maxLength: validators.PASSWORD_MAX_LENGTH,
                                            }
                                        ),
                                        validators.PASSWORD_MAX_LENGTH
                                    )
                                )}
                                customErrorText={passwordErrorText}
                            />
                        </div>

                        <p className={css.dataProtectionInfo}>
                            <IconLockL />
                            <FormattedMessage id="ContactDetailsForm.dataProtectionInfo" />
                        </p>

                        <div className={css.bottomWrapper}>
                            {genericError}
                            <Button
                                type="button"
                                inProgress={inProgress}
                                ready={pristineSinceLastSubmit}
                                disabled={submitDisabled}
                                onClick={() => {
                                    submittedValuesRef.current = values;
                                    props.onSubmit(values);
                                }}
                            >
                                <FormattedMessage id="ContactDetailsForm.saveChanges" />
                            </Button>
                        </div>
                    </Form>
                );
            }}
        />
    );
};

const { bool, func, string } = PropTypes;

ContactDetailsForm.propTypes = {
    rootClassName: string,
    className: string,
    formId: string,
    saveEmailError: propTypes.error,
    savePhoneNumberError: propTypes.error,
    inProgress: bool,
    intl: intlShape.isRequired,
    onResendVerificationEmail: func.isRequired,
    ready: bool.isRequired,
    sendVerificationEmailError: propTypes.error,
    sendVerificationEmailInProgress: bool,

    resetPasswordInProgress: bool,
    resetPasswordError: propTypes.error,
};

export default ContactDetailsForm;
